import Joi from "joi";
import cardPacksJson from "../data/cardPacks.json";
import playablesArrayJson from "../data/playablesArray.json";
import {
  CardPack,
  Playable,
  PlayableId,
  CardKinds,
  CardLevels,
  ResourceKinds,
} from "../types";

// Joi schemas for validation
const cardTemplateSchema = Joi.object({
  name: Joi.string().required(),
});

const cardSchema = Joi.object({
  template: cardTemplateSchema.required(),
  weight: Joi.number().required(),
});

const cardPackSchema = Joi.object({
  name: Joi.string().required(),
  description: Joi.string().required(),
  cost: Joi.number().required(),
  cards: Joi.array().items(cardSchema).required(),
  required: Joi.array()
    .items(Joi.string().valid(...ResourceKinds))
    .required(),
});

const dynamicResourcesSchema = Joi.object({
  money: Joi.number().required(),
  food: Joi.number().required(),
  fuel: Joi.number().required(),
  ore: Joi.number().required(),
  fight: Joi.number().required(),
});

const combinesSchema = Joi.object({
  ownMatchString: Joi.string().required(),
  withMatchStrings: Joi.array().items(Joi.string()).required(),
  into: Joi.string().allow(null).optional(),
});

const playableSchema = Joi.object({
  name: Joi.string().required(),
  label: Joi.string().required(),
  kind: Joi.string()
    .valid(...CardKinds)
    .required(),
  level: Joi.number()
    .valid(...CardLevels)
    .required(),
  resources: dynamicResourcesSchema.required(),
  requiredUnlock: Joi.array()
    .items(Joi.string().valid(...ResourceKinds))
    .required(),
  providesUnlock: Joi.array()
    .items(Joi.string().valid(...ResourceKinds))
    .required(),
  combines: combinesSchema.required(),
});

export function loadCardPacks(): CardPack[] {
  // Validate with typed result
  const { error, value } = Joi.array()
    .items(cardPackSchema)
    .validate(cardPacksJson) as Joi.ValidationResult<CardPack[]>;
  if (error) throw error;

  return value;
}

export function loadPlayables(): Map<PlayableId, Playable> {
  // Validate with typed result
  const { error, value } = Joi.array()
    .items(playableSchema)
    .validate(playablesArrayJson) as Joi.ValidationResult<Playable[]>;
  if (error) throw error;

  const playablesMap = new Map<PlayableId, Playable>();

  value.forEach((playable) => {
    playablesMap.set(playable.name as PlayableId, playable);
  });

  return playablesMap;
}

// Alternative: If you prefer to have playables exported as an array from the ODS
export function loadPlayablesFromArray(
  playablesArray: unknown[],
): Map<PlayableId, Playable> {
  // Validate with typed result
  const { error, value } = Joi.array()
    .items(playableSchema)
    .validate(playablesArray) as Joi.ValidationResult<Playable[]>;
  if (error) {
    throw new Error(`Playables array validation failed: ${error.message}`);
  }

  const playablesMap = new Map<PlayableId, Playable>();

  value.forEach((playable) => {
    playablesMap.set(playable.name as PlayableId, playable);
  });

  return playablesMap;
}
